import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import React, { useCallback, useRef } from "react";
import useVacancyApplicantActivityFlow from "./hooks/useVacancyApplicantActivityFlow";
import LoadPage from "@/components/LoadPage/LoadPage";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import QRCode from "react-qr-code";
import { Accordion } from "rsuite";
import i18n from "@/i18n";
import DataBusDefaults from "@/services/DataBusDefaults";
import { createVacancyApplicantActionFlowPDF } from "../helper/VacancyApplicantQrPdfExporter";
import { ActionFlowEntryPublicData } from "@/modules/action-flow/model/action-flow.interface";
import ModalManager from "@/components/ModalComponent/ModalManager";
import DebugDataComponent from "@/debug/DebugDataComponent";
import VacancyOfferUtils from '../vacancy-applicant/utils/VacancyOfferUtils';

type Props = {
  activity: APActivity;
  constants: ActivityApplicationConstants;
};

const VacancyApplicantActionFlow = (props: Props) => {
  const { activity } = props;

  const {
    error,
    isEmpty,
    isLoading,
    offerActionFlowEntry,
    warning,
    createSelfDisclosureActionFlow,
    deleteSelfDisclosureActionFlow,
  } = useVacancyApplicantActivityFlow(props);

  const qrReference = useRef<HTMLDivElement>();

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="offer-action-flow__loading">
          <LoadPage />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="offer-action-flow__empty __flex __align-center __justify-center">
          <BFButton
            appearance="link"
            onClick={() => {
              createSelfDisclosureActionFlow();
            }}
          >
            {i18n.t(
              "apTemplate:Vacancy.OfferActionFlow.Create",
              "QR Code erstellen"
            )}
          </BFButton>
        </div>
      );
    }

    if (error) {
      return (
        <div className="offer-action-flow__error">
          <div>
            {error || "An unknown error occurred. Please try again later."}
          </div>
        </div>
      );
    }

    if (offerActionFlowEntry) {
      const entryData: ActionFlowEntryPublicData =
        offerActionFlowEntry.data as ActionFlowEntryPublicData;

      const tokenLink = VacancyOfferUtils.getTokenLink(offerActionFlowEntry);

      return (
        <>
          <div className="offer-action-flow__content">
            <DebugDataComponent data={entryData} />
            <div className="__flex __align-center padding-top-20">
              <div className="__card">
                <div ref={qrReference}>
                  <QRCode
                    style={{ height: "auto", width: "100px" }}
                    value={tokenLink}
                  />
                </div>
              </div>
              <div>
                <BFButton
                  appearance="link"
                  onClick={() => {
                    navigator.clipboard.writeText(tokenLink);
                    DataBusDefaults.toast({
                      type: "success",
                      text: i18n.t(
                        "apTemplate:Vacancy.OfferActionFlow.QR.LinkCopied",
                        "Link in die Zwischenablage kopiert"
                      ),
                    });
                  }}
                >
                  {tokenLink}
                </BFButton>
              </div>
              <div className="__flex __flex-column margin-left-auto">
                <BFButton
                  appearance="link"
                  onClick={() => {
                    ModalManager.confirm({
                      title: i18n.t(
                        "apTemplate:Vacancy.OfferActionFlow.QR.DeleteTitle",
                        "QR Code löschen"
                      ),
                      message: i18n.t(
                        "apTemplate:Vacancy.OfferActionFlow.QR.DeleteMessage",
                        "Möchten Sie diesen QR Code wirklich löschen?"
                      ),
                      confirmButtonText: i18n.t(
                        "apTemplate:Vacancy.OfferActionFlow.QR.DeleteBtn",
                        "Löschen"
                      ),
                      onConfirm: () => {
                        deleteSelfDisclosureActionFlow(
                          offerActionFlowEntry._id
                        );
                      },
                    });
                  }}
                >
                  {i18n.t(
                    "apTemplate:Vacancy.OfferActionFlow.QR.Archive",
                    "QR Code löschen"
                  )}
                </BFButton>
                <BFButton
                  appearance="link"
                  onClick={() => {
                    createVacancyApplicantActionFlowPDF({
                      activity,
                      offerActionFlowEntry,
                      qrReference,
                    });
                  }}
                >
                  {i18n.t(
                    "apTemplate:Vacancy.OfferActionFlow.QR.PdfExport",
                    "QR Code als PDF exportieren"
                  )}
                </BFButton>
              </div>
            </div>
          </div>
          {warning && (
            <div className="offer-action-flow__warning">
              <div>Warning: {warning || "This is a generic warning."}</div>
            </div>
          )}
        </>
      );
    }

    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isEmpty, error, warning, offerActionFlowEntry]);

  return (
    <div className="offer-action-flow __card margin-top-20">
      <Accordion bordered>
        <Accordion.Panel
          header={
            <div className="__h3">
              {i18n.t(
                "apTemplate:Vacancy.OfferActionFlow.QR.Title",
                "QR Code Mieterselbstauskunft"
              )}
            </div>
          }
        >
          {renderContent()}
        </Accordion.Panel>
      </Accordion>
    </div>
  );
};

export default VacancyApplicantActionFlow;
